import { Dispatch, FunctionComponent, memo, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticContentTagListOrderBy, useContentTagListQuery } from '../../../../services';
import { getChannelDomain, getWebAppUrl, layoutPath, OrderDirection } from '../../../../shared';
import { ChartWrapper, DataRowItem, Loader, TableChart, TableChartHeader } from '../../helpers';
import { ContentProps } from '../Content';

export const ContentTagList: FunctionComponent<ContentProps> = memo(
  ({ from, to, channelId, platform, categoryId, organisationTags }) => {
    const { t } = useTranslation();

    const [orderBy, setOrderBy] = useState<AnalyticContentTagListOrderBy>(
      AnalyticContentTagListOrderBy.PUBLISHED
    );

    const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC);

    const [page, setPage] = useState<number>(1);

    const { data: contentTagList, isFetching: isFetchingContentTagList } = useContentTagListQuery({
      from,
      to,
      channelId,
      platform,
      categoryId,
      organisationTags,
      orderBy,
      orderDirection,
      page,
    });

    const renderChart = useMemo(() => {
      if (!contentTagList) {
        return <Loader />;
      }

      const { items, pageInfo } = contentTagList;

      const data: DataRowItem[][] = items.map(({ label, payload }) => {
        const { tag, published, views, engagement } = payload;

        const { id, name, type } = tag;

        return [
          { value: name, url: `${getWebAppUrl(getChannelDomain())}${layoutPath(`/tags/${id}`)}` },
          { value: t(`organisationTagType.${type}`) },
          { value: published },
          { value: views },
          { value: engagement },
        ];
      });

      const headers: TableChartHeader[] = [
        { label: t('contentTagListLabels.name') },
        { label: t('contentTagListLabels.type') },
        {
          label: t('contentTagListLabels.published'),
          orderBy: AnalyticContentTagListOrderBy.PUBLISHED,
        },
        {
          label: t('contentTagListLabels.views'),
          orderBy: AnalyticContentTagListOrderBy.VIEWS,
        },
        {
          label: t('contentTagListLabels.engagement'),
          orderBy: AnalyticContentTagListOrderBy.ENGAGEMENT,
        },
      ];

      return (
        <TableChart
          sticky
          headers={headers}
          data={data}
          pageInfo={pageInfo}
          setPage={setPage}
          orderBy={orderBy}
          setOrderBy={setOrderBy as Dispatch<SetStateAction<string>>}
          orderDirection={orderDirection}
          setOrderDirection={setOrderDirection}
          loading={isFetchingContentTagList}
        />
      );
    }, [contentTagList, isFetchingContentTagList, orderBy, orderDirection, t]);

    return (
      <ChartWrapper
        typeLocaliseKey={'chartTypes.content-tag-list'}
        infoLocaliseKey={'contentTagList.info'}
      >
        {renderChart}
      </ChartWrapper>
    );
  }
);
